import type { FC, ReactNode } from 'react'
import type { Bi, Gender, NounTable, Purusha, Vacana, Vibhakti } from '@/sanskrit/lib/sanskrit/types'
import type { StemClass } from '@/sanskrit/lib/sanskrit/nouns'

import { useRouter } from 'next/router'
import { useEffect, useMemo, useState } from 'react'
import { EyeOff, Eye, RotateCcw } from 'lucide-react'
import { cn } from '@/sanskrit/lib/utils'
import { Seo } from '@/sanskrit/components/generals/seo'
import { Layout } from '@/sanskrit/components/generals/layout'
import { SanskritGate } from '@/components/plans/sanskrit-gate'
import { Chip, PageHeader, Segmented, StatusBadge } from '@/sanskrit/components/vani/bits'
import { useAppState, useT } from '@/sanskrit/components/providers/app-state'
import { MODEL_PARADIGMS, PRONOUN_PARADIGMS, STEM_CLASSES, baseOf, declineStem, getNounParadigm } from '@/sanskrit/lib/sanskrit/nouns'
import { VOCABULARY, getVocab } from '@/sanskrit/lib/sanskrit/vocabulary'
import { getParadigm } from '@/sanskrit/lib/sanskrit/paradigms'
import { splitForHighlight } from '@/sanskrit/lib/sanskrit/script'
import { recordAnswer } from '@/sanskrit/lib/progress/engine'
import { seededShuffle } from '@/sanskrit/lib/random'
import { VACANAS, VIBHAKTIS } from '@/sanskrit/lib/sanskrit/types'
import { GENDER_LABEL, VACANA_LABEL, VIBHAKTI_LABEL } from '@/sanskrit/lib/sanskrit/labels'
import { NounTableView, cellKey } from '@/sanskrit/components/grammar/tables'
import AudioButton from '@/sanskrit/components/vani/audio-button'
import Skt from '@/sanskrit/components/vani/skt'

interface WordChoice {
    key: string
    label: string
    meaning: Bi
    genders: Gender[]
    table: (g: Gender) => NounTable | null
    stemFor: (g: Gender) => { stem: string; cls?: StemClass } | null
    purusha: Purusha
    vocabId?: string
    note?: Bi
}

const MATRA_TO_INDEP: Record<string, string> = { 'ा': 'आ', 'ि': 'इ', 'ी': 'ई', 'ु': 'उ', 'ू': 'ऊ', 'ृ': 'ऋ', 'ॄ': 'ॠ', 'े': 'ए', 'ै': 'ऐ', 'ो': 'ओ', 'ौ': 'औ' }

const endingDisplay = (ending: string): string => {
    if (!ending) return '∅'
    const first = ending[0]
    if (MATRA_TO_INDEP[first]) return MATRA_TO_INDEP[first] + ending.slice(1)
    if (first === '्') return ending.slice(1)
    return ending
}

const choices = (): WordChoice[] => {
    const out: WordChoice[] = []

    for (const p of MODEL_PARADIGMS) {
        out.push({
            key: `p:${p.id}`,
            label: `${p.lemma} (${p.cls ? STEM_CLASSES[p.cls].sa : p.stem})`,
            meaning: p.meaning,
            genders: [p.gender],
            table: () => p.table,
            stemFor: () => (p.cls ? { stem: p.stem, cls: p.cls } : null),
            purusha: 'prathama',
            note: p.note
        })
    }

    const pronounGroups = new Map<string, typeof PRONOUN_PARADIGMS>()
    for (const p of PRONOUN_PARADIGMS) {
        const list = pronounGroups.get(p.stem) ?? []
        list.push(p)
        pronounGroups.set(p.stem, list)
    }

    for (const [stem, list] of pronounGroups) {
        out.push({
            key: `p:${list[0].id}`,
            label: `${stem} (${list.map(x => x.lemma).join(' / ')})`,
            meaning: list[0].meaning,
            genders: list.map(x => x.gender),
            table: g => (list.find(x => x.gender === g) ?? list[0]).table,
            stemFor: () => null,
            purusha: list[0].purusha ?? 'prathama',
            note: list[0].note
        })
    }

    for (const v of VOCABULARY) {
        if (v.pos === 'noun' && v.cls && v.stem) {
            out.push({ key: `v:${v.id}`, label: v.sa, meaning: v.meaning, genders: [v.gender ?? 'm'], table: () => declineStem(v.stem as string, v.cls as StemClass), stemFor: () => ({ stem: v.stem as string, cls: v.cls }), purusha: 'prathama', vocabId: v.id, note: v.note })
        }
        else if (v.pos === 'noun' && v.paradigmId) {
            out.push({ key: `v:${v.id}`, label: v.sa, meaning: v.meaning, genders: [v.gender ?? 'm'], table: () => getNounParadigm(v.paradigmId as string)?.table ?? null, stemFor: () => null, purusha: 'prathama', vocabId: v.id })
        }
        else if (v.pos === 'adjective' && v.stem && !v.adjGenders && !['purva', 'pascima', 'uttara-dik', 'daksina'].includes(v.id)) {
            const fem = v.fem
            out.push({
                key: `v:${v.id}`,
                label: `${v.stem} (${v.stem}ः / ${fem?.stem ?? v.stem + 'ा'} / ${v.stem}म्)`,
                meaning: v.meaning,
                genders: ['m', 'f', 'n'],
                table: g => (g === 'm' ? declineStem(v.stem as string, 'a-m') : g === 'n' ? declineStem(v.stem as string, 'a-n') : declineStem(fem?.stem ?? `${v.stem}ा`, fem?.cls ?? 'aa-f')),
                stemFor: g => (g === 'm' ? { stem: v.stem as string, cls: 'a-m' } : g === 'n' ? { stem: v.stem as string, cls: 'a-n' } : { stem: fem?.stem ?? `${v.stem}ा`, cls: fem?.cls ?? 'aa-f' }),
                purusha: 'prathama',
                vocabId: v.id,
                note: v.note
            })
        }
    }

    return out
}

const joinPhrase = (a: string, b: string): string => (a.endsWith('म्') && /^[क-ह]/.test(b) ? `${a.slice(0, -2)}ं ${b}` : `${a} ${b}`)

const NUMBER_EN: Record<Vacana, (m: string) => string> = { eka: m => m, dvi: m => `two ${m}s`, bahu: m => `${m}s` }
const NUMBER_HI: Record<Vacana, (m: string) => string> = { eka: m => m, dvi: m => `दो ${m}`, bahu: m => `अनेक ${m}` }

const exampleFor = (form: string, vib: Vibhakti, vac: Vacana, purusha: Purusha, meaning: Bi): { sa: string; hi: string; en: string } | null => {
    const m = meaning.en.split(',')[0].replace(/\s*\(.*\)$/, '')
    const mh = meaning.hi.split(',')[0]
    const X = NUMBER_EN[vac](m)
    const Xh = NUMBER_HI[vac](mh)

    switch (vib) {
        case 1: {
            const asti = getParadigm('as', 'lat', 'P')?.forms[purusha][vac][0] ?? 'अस्ति'
            return { sa: `${form} ${asti}।`, hi: `${Xh} है/हैं।`, en: purusha === 'prathama' ? `There ${vac === 'eka' ? 'is' : 'are'} ${vac === 'eka' ? 'a ' : ''}${X}.` : purusha === 'uttama' ? (vac === 'eka' ? 'I am (here).' : 'We are (here).') : (vac === 'eka' ? 'You are (here).' : 'You are (here), all of you.') }
        }
        case 2:
            return { sa: `सः ${joinPhrase(form, 'पश्यति')}।`, hi: `वह ${Xh} को देखता है।`, en: `He sees ${X}.` }
        case 3:
            return { sa: `सः ${joinPhrase(form, 'सह')} गच्छति।`, hi: `वह ${Xh} के साथ जाता है।`, en: `He goes with ${X}.` }
        case 4:
            return { sa: `सः ${joinPhrase(form, 'फलं')} ददाति।`, hi: `वह ${Xh} को फल देता है।`, en: `He gives a fruit to ${X}.` }
        case 5:
            return { sa: `सः ${form} आगच्छति।`, hi: `वह ${Xh} से आता है।`, en: `He comes from ${X}.` }
        case 6:
            return { sa: `एतत् ${joinPhrase(form, 'गृहम्')}।`, hi: `यह ${Xh} का घर है।`, en: `This is the house of ${X}.` }
        case 7:
            return { sa: `${joinPhrase(form, 'तस्य')} स्नेहः अस्ति।`, hi: `उसे ${Xh} से स्नेह है।`, en: `He has affection for ${X}.` }
        case 8:
            return { sa: `हे ${form}!`, hi: `हे ${Xh}!`, en: `O ${X}!` }
    }
}

const ShabdarupaPage: FC = (): ReactNode => {
    const t = useT()
    const router = useRouter()
    const { updateProgress } = useAppState()
    const all = useMemo(choices, [])
    const [key, setKey] = useState('p:rama')
    const [gender, setGender] = useState<Gender>('m')
    const [sel, setSel] = useState<{ vib: Vibhakti; v: Vacana }>({ vib: 1, v: 'eka' })
    const [memory, setMemory] = useState(false)
    const [revealed, setRevealed] = useState<Set<string>>(new Set())
    const [quizCell, setQuizCell] = useState<{ vib: Vibhakti; v: Vacana } | null>(null)
    const [score, setScore] = useState({ right: 0, tried: 0 })
    const [wrongPick, setWrongPick] = useState<string | null>(null)

    useEffect(() => {
        if (!router.isReady) return
        const q = router.query
        if (typeof q.word === 'string') {
            if (all.some(c => c.key === q.word)) setKey(q.word)
            else if (q.word.startsWith('p:')) {
                const pronoun = PRONOUN_PARADIGMS.find(p => p.id === (q.word as string).slice(2))
                const groupHead = pronoun ? PRONOUN_PARADIGMS.find(p => p.stem === pronoun.stem) : undefined
                if (groupHead) setKey(`p:${groupHead.id}`)
            }
        }
        if (q.gender === 'm' || q.gender === 'f' || q.gender === 'n') setGender(q.gender)
        const vib = Number(q.vib)
        if (vib >= 1 && vib <= 8 && typeof q.v === 'string' && (VACANAS as string[]).includes(q.v)) setSel({ vib: vib as Vibhakti, v: q.v as Vacana })
    }, [router.isReady, router.query, all])

    const choice = all.find(c => c.key === key) ?? all[0]
    const g = choice.genders.includes(gender) ? gender : choice.genders[0]
    const table = choice.table(g)
    const stemInfo = choice.stemFor(g)

    useEffect(() => {
        setRevealed(new Set())
        setQuizCell(null)
        setScore({ right: 0, tried: 0 })
    }, [key, g, memory])

    if (!table) return null

    const cell = table[sel.vib][sel.v]
    const form = cell[0]
    const base = stemInfo?.cls ? baseOf(stemInfo.stem, stemInfo.cls) : null
    const [kept, changed] = base && form ? splitForHighlight(form, base.length) : [form, '']
    const ending = base && form ? form.slice(base.length) : ''
    const example = form ? exampleFor(form, sel.vib, sel.v, choice.purusha, choice.meaning) : null
    const vibLabel = VIBHAKTI_LABEL[sel.vib]
    const hidden = memory ? new Set(VIBHAKTIS.flatMap(vib => VACANAS.map(v => cellKey(vib, v)))) : undefined

    const quizOptions = quizCell ? seededShuffle(Array.from(new Set([table[quizCell.vib][quizCell.v][0], ...seededShuffle(VIBHAKTIS.flatMap(vib => VACANAS.map(v => table[vib][v][0])).filter(Boolean), `${key}${quizCell.vib}${quizCell.v}`).slice(0, 5)])).slice(0, 4), `${quizCell.vib}${quizCell.v}`) : []
    const quizAnswer = quizCell ? table[quizCell.vib][quizCell.v][0] : ''

    const pickQuiz = (option: string) => {
        if (!quizCell) return
        const ok = option === quizAnswer || table[quizCell.vib][quizCell.v].includes(option)
        setScore(s => ({ right: s.right + (ok ? 1 : 0), tried: s.tried + 1 }))
        setRevealed(r => new Set(r).add(cellKey(quizCell.vib, quizCell.v)))
        if (choice.vocabId && quizCell.vib !== 8) {
            updateProgress(p => recordAnswer(p, { item: `noun:${choice.vocabId}:${quizCell.vib}:${quizCell.v}`, skill: 'declension', ok, confusions: ok ? undefined : [`vibhakti:${quizCell.vib}>${VIBHAKTIS.find(vib => VACANAS.some(v => table[vib][v][0] === option)) ?? quizCell.vib}`] }))
        }
        if (!ok) {
            setWrongPick(option)
            window.setTimeout(() => setWrongPick(null), 900)
        }
        setSel(quizCell)
        setQuizCell(null)
    }

    return (
        <Layout>
            <Seo title='Shabdarūpa lab' description='Generate any Sanskrit declension: choose a word, gender, case and number to see the form, its role and how it is built, or fill the table from memory.' />

            <SanskritGate what='The shabdarūpa lab'>
            <PageHeader
                sa='शब्दरूपप्रयोगशाला'
                title={{ hi: 'शब्दरूप प्रयोगशाला', en: 'Shabdarūpa laboratory' }}
                subtitle={{ hi: 'शब्द → लिङ्ग → विभक्ति → वचन। हर खाने को छूकर उसकी भूमिका, बनावट और उदाहरण देखिए।', en: 'Word → gender → case → number. Tap any cell for its role, its make-up and an example.' }}
            />

            <div className='mx-auto grid max-w-7xl gap-6 px-4 pb-16 sm:px-6 lg:grid-cols-[1fr_24rem]'>
                <div className='min-w-0 space-y-4'>
                    <div className='flex flex-wrap items-center gap-3 rounded-3xl border border-border bg-card p-4'>
                        <label className='flex min-w-0 flex-1 flex-col text-xs font-semibold text-muted-foreground'>
                            <span><span lang='sa' className='deva'>शब्दः</span> · {t({ hi: 'शब्द', en: 'Word' })}</span>
                            <select value={choice.key} onChange={e => setKey(e.target.value)} className='deva mt-1 h-11 w-full rounded-xl border border-input bg-background px-3 text-base text-foreground'>
                                <optgroup label={t({ hi: 'आदर्श शब्द (हर वर्ग का नमूना)', en: 'Model words (one per class)' })}>
                                    {all.filter(c => c.key.startsWith('p:') && !PRONOUN_PARADIGMS.some(p => `p:${p.id}` === c.key)).map(c => <option key={c.key} value={c.key}>{c.label} · {t(c.meaning)}</option>)}
                                </optgroup>
                                <optgroup label={t({ hi: 'सर्वनाम', en: 'Pronouns' })}>
                                    {all.filter(c => PRONOUN_PARADIGMS.some(p => `p:${p.id}` === c.key)).map(c => <option key={c.key} value={c.key}>{c.label} · {t(c.meaning)}</option>)}
                                </optgroup>
                                <optgroup label={t({ hi: 'शब्दकोश के शब्द', en: 'Dictionary words' })}>
                                    {all.filter(c => c.key.startsWith('v:')).map(c => <option key={c.key} value={c.key}>{c.label} · {t(c.meaning)}</option>)}
                                </optgroup>
                            </select>
                        </label>
                        {choice.genders.length > 1 && (
                            <div>
                                <p className='mb-1 text-xs font-semibold text-muted-foreground'><span lang='sa' className='deva'>लिङ्गम्</span></p>
                                <Segmented size='sm' label={t({ hi: 'लिङ्ग', en: 'Gender' })} value={g} onChange={setGender} options={choice.genders.map(x => ({ value: x, label: t({ hi: GENDER_LABEL[x].hi, en: GENDER_LABEL[x].en }) }))} />
                            </div>
                        )}
                        <button type='button' onClick={() => setMemory(m => !m)} aria-pressed={memory} className={cn('inline-flex h-11 items-center gap-2 self-end rounded-xl border-2 px-3 text-sm font-semibold', memory ? 'border-saffron-strong bg-saffron-soft' : 'border-border bg-background')}>
                            {memory ? <Eye className='size-4' /> : <EyeOff className='size-4' />}
                            {memory ? t({ hi: 'तालिका दिखाएँ', en: 'Show table' }) : t({ hi: 'याद से भरें', en: 'Fill from memory' })}
                        </button>
                    </div>

                    <div className='flex flex-wrap items-center gap-2'>
                        {stemInfo?.cls && <Chip tone='ink'><span lang='sa' className='deva'>{STEM_CLASSES[stemInfo.cls].sa}</span></Chip>}
                        <Chip tone='saffron'>{t({ hi: GENDER_LABEL[g].hi, en: GENDER_LABEL[g].en })}</Chip>
                        <StatusBadge status='verified' />
                        {memory && <Chip tone='tulsi'>{t({ hi: `स्कोर ${score.right}/${score.tried}`, en: `Score ${score.right}/${score.tried}` })}</Chip>}
                        {memory && revealed.size > 0 && (
                            <button type='button' onClick={() => { setRevealed(new Set()); setScore({ right: 0, tried: 0 }) }} className='inline-flex items-center gap-1 text-sm font-semibold text-muted-foreground'>
                                <RotateCcw className='size-3.5' />
                                {t({ hi: 'फिर से', en: 'Reset' })}
                            </button>
                        )}
                    </div>

                    <NounTableView
                        table={table}
                        highlight={memory ? quizCell : sel}
                        onCellClick={(vib, v) => {
                            if (memory && !revealed.has(cellKey(vib, v))) setQuizCell({ vib, v })
                            else setSel({ vib, v })
                        }}
                        hidden={hidden}
                        revealed={revealed}
                    />

                    {memory && quizCell && (
                        <div className='rounded-3xl border-2 border-saffron bg-card p-4'>
                            <p className='font-semibold'>
                                <span lang='sa' className='deva'>{VIBHAKTI_LABEL[quizCell.vib].sa}, {VACANA_LABEL[quizCell.v].sa}</span> · {t({ hi: 'सही रूप चुनिए', en: 'pick the right form' })}
                            </p>
                            <div className='mt-3 grid grid-cols-2 gap-2'>
                                {quizOptions.map(o => (
                                    <button key={o} type='button' onClick={() => pickQuiz(o)} className='rounded-xl border-2 border-border bg-background px-3 py-2 hover:border-saffron'>
                                        <span lang='sa' className='deva text-lg font-semibold'>{quizCell.vib === 8 ? 'हे ' : ''}{o}</span>
                                    </button>
                                ))}
                            </div>
                        </div>
                    )}
                    {wrongPick && <p className='rounded-xl bg-terracotta-soft px-3 py-2 text-sm text-terracotta'>{t({ hi: `${wrongPick} नहीं; सही रूप तालिका में दिख रहा है।`, en: `Not ${wrongPick}; the right form is now shown in the table.` })}</p>}
                    {choice.note && <p className='rounded-2xl bg-saffron-soft/70 px-4 py-3 text-sm'>{t(choice.note)}</p>}
                </div>

                <aside className='space-y-4 lg:sticky lg:top-20 lg:self-start'>
                    <div className='rounded-3xl bg-ink p-5 text-primary-foreground'>
                        <p className='text-xs font-semibold tracking-[0.2em] text-saffron uppercase'>{vibLabel.sa} · {VACANA_LABEL[sel.v].sa}</p>
                        <div className='mt-2 flex items-center justify-between gap-3'>
                            <p lang='sa' className='deva text-4xl font-bold'>
                                {sel.vib === 8 && form ? 'हे ' : ''}{kept}<span className='text-saffron'>{changed}</span>
                            </p>
                            {form && <AudioButton text={form} className='border-white/20 bg-white/10 text-white hover:bg-white/20' />}
                        </div>
                        {cell.length > 1 && <p lang='sa' className='deva mt-1 text-sm text-primary-foreground/75'>{t({ hi: 'वैकल्पिक रूप:', en: 'Also:' })} {cell.slice(1).join(' / ')}</p>}
                        {!form && <p className='mt-1 text-sm text-primary-foreground/75'>{t({ hi: 'इस शब्द का यह रूप नहीं होता।', en: 'This word has no form here.' })}</p>}
                        {base && form && (
                            <p className='mt-3 text-sm text-primary-foreground/80'>
                                <span lang='sa' className='deva'>{base}</span> + <span lang='sa' className='deva font-semibold text-saffron'>{endingDisplay(ending)}</span>
                                {/ण/.test(ending) && !/ण/.test(base) && <span className='block text-xs text-primary-foreground/65'>{t({ hi: 'र/ष/ऋ के कारण न → ण (णत्व)।', en: 'न → ण because of a preceding र, ष or ऋ (the णत्व rule).' })}</span>}
                            </p>
                        )}
                    </div>

                    <div className='rounded-3xl border border-border bg-card p-5'>
                        <p className='text-xs font-semibold tracking-wide text-muted-foreground uppercase'>{t({ hi: 'इस विभक्ति की भूमिका', en: 'What this case does' })}</p>
                        <p className='mt-1 font-semibold text-foreground'><span lang='sa' className='deva'>{vibLabel.karaka}</span> · {t(vibLabel.role)}</p>
                        <p className='mt-1 text-sm text-muted-foreground'>{t({ hi: `चिह्न: ${vibLabel.marker.hi} · प्रश्न: ${vibLabel.question.hi}`, en: `Marker: ${vibLabel.marker.en} · Asks: ${vibLabel.question.en}` })}</p>
                        <p className='mt-2 text-sm text-muted-foreground'>{t(vibLabel.desc)}</p>
                    </div>

                    {example && form && (
                        <div className='rounded-3xl border border-border bg-card p-5'>
                            <p className='text-xs font-semibold tracking-wide text-muted-foreground uppercase'>{t({ hi: 'उदाहरण वाक्य', en: 'Example sentence' })}</p>
                            <div className='mt-2 flex items-start justify-between gap-2'>
                                <Skt text={example.sa} className='text-2xl font-semibold text-ink' />
                                <AudioButton text={example.sa} size='sm' />
                            </div>
                            <p className='mt-1 text-sm text-muted-foreground'>{t({ hi: example.hi, en: example.en })}</p>
                        </div>
                    )}
                </aside>
            </div>
            </SanskritGate>
        </Layout>
    )
}

export default ShabdarupaPage
